import React from "react";
import axios from "axios";
import { useLoaderData, useSubmit, redirect } from "react-router";
import { Card, Row, Col, Container, Button } from "react-bootstrap";
import {
    SubscriptionCreateSchema,
    SubscriptionUpdateSchema,
} from "schema/subscription";
import {
    renderLoadingProgressToast,
    renderSavingProgressToast,
    renderSimpleToast,
} from "components/shared/Toasts";
import { getPlatformShortName } from "utils/CapacitorUtil";
import { errorCode } from "components/exception/ErrorCode";

export async function loader({ params }) {
    const data = await renderLoadingProgressToast(
        axios.get(`subscription/checkout/${params.planId}`)
    )
        .then((response) => response.data)
        .catch((error) => {
            if (
                error.response?.data?.error_code ===
                errorCode.existingSubscription
            ) {
                renderSimpleToast(error.response.data.description);
                return redirect("/subscription/pricing/upgrade");
            }
            if (
                error.response?.data?.error_code === errorCode.freeTrialClaimed
            ) {
                return redirect("/subscription/pricing/upgrade");
            }
            throw error;
        });
    return data;
}

export async function action({ request }) {
    const platform = getPlatformShortName();
    const updates = await request.json();
    if (request.method === "POST") {
        const approve_link = await renderSavingProgressToast(
            axios.post(
                `subscription/${platform}`,
                SubscriptionCreateSchema.cast(updates)
            )
        ).then((resp) => resp.data);
        return redirect(approve_link);
    } else if (request.method === "PUT") {
        const approve_link = await renderSavingProgressToast(
            axios.put(
                `subscription/${platform}`,
                SubscriptionUpdateSchema.cast(updates)
            )
        ).then((resp) => resp.data);
        return redirect(approve_link);
    }

    return null;
}

const Checkout = () => {
    const { user_subscription, order_summary, payment_methods } =
        useLoaderData();
    const submit = useSubmit();

    const onProcessSubscription = (payment_gateway, pmtGatewayPlanId) => () => {
        if (user_subscription === null) {
            submit(
                {
                    plan_type_id: order_summary.plan_type_id,
                    payment_gateway: payment_gateway,
                    payment_gateway_plan_id: pmtGatewayPlanId,
                },
                { method: "POST", encType: "application/json" }
            );
        } else {
            submit(
                {
                    sub_id: user_subscription.sub_id,
                    plan_type_id: order_summary.plan_type_id,
                    payment_gateway: payment_gateway,
                    payment_gateway_plan_id: pmtGatewayPlanId,
                    payment_gateway_sub_id:
                        user_subscription.payment_gateway_sub_id,
                },
                { method: "PUT", encType: "application/json" }
            );
        }
    };

    return (
        <Container className="my-2">
            <h1 className="text-center">Checkout</h1>
            <Row>
                <Col md={6} className="mx-auto">
                    <Card className="my-3">
                        <Card.Header>Order Summary</Card.Header>
                        <Card.Body>
                            <Card.Text className="d-flex">
                                <span
                                    className="me-auto"
                                    data-testid="order_summary_description"
                                >
                                    {order_summary.plan_name}
                                </span>
                                <b data-testid="order_summary_amount">
                                    {`${order_summary.currency} ${order_summary.amount} per ${order_summary.interval_unit}`}
                                </b>
                            </Card.Text>
                            <ul>
                                {order_summary.features.map((el, i) => (
                                    <li key={i}>{el}</li>
                                ))}
                            </ul>
                            <Card.Text>
                                <span className="text-body-secondary">
                                    {order_summary.remarks}
                                </span>
                            </Card.Text>
                            <hr />
                            <Card.Text data-testid="order_summary_total">
                                {`Total: ${order_summary.currency} ${order_summary.amount}`}
                            </Card.Text>
                        </Card.Body>
                    </Card>
                    <Card
                        className="my-3"
                        data-testid="card_revise_subscription"
                    >
                        <Card.Header>Change subscription</Card.Header>
                        <Card.Body>
                            {payment_methods
                                .filter((p) => p.payment_gateway === "PAYPAL")
                                .map((p) => (
                                    <div
                                        key={p.client_id}
                                        className="d-flex gap-2 align-items-center"
                                    >
                                        <Button
                                            data-testid="btn_revise_subscription"
                                            onClick={onProcessSubscription(
                                                p.payment_gateway,
                                                p.payment_gateway_plan_id
                                            )}
                                        >
                                            Subscribe
                                        </Button>
                                        <Card.Text>
                                            You will be redirected to{" "}
                                            {p.payment_gateway} for approval
                                        </Card.Text>
                                    </div>
                                ))}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Checkout;
